import type { TFunction } from 'i18next';

/**
 * Values are persisted and sent on the wire (e.g. `location.siteType.category` in JSON) exactly as these
 * strings. Uppercase SCREAMING_SNAKE matches the backend contract—do not change without an API migration.
 * For UI strings, use the `site_type.categories.*` / `site_type.subcategories.*` translation keys (lowercase).
 */
export enum SiteTypeCategory {
  RESIDENTIAL = 'RESIDENTIAL',
  WORKPLACE = 'WORKPLACE',
  RETAIL_AND_HOSPITALITY = 'RETAIL_AND_HOSPITALITY',
  PUBLIC_PARKING = 'PUBLIC_PARKING',
  COMMUNITY_AND_CIVIC = 'COMMUNITY_AND_CIVIC',
  FLEET_DEPOT = 'FLEET_DEPOT',
  TRANSIT_AND_TRAVEL = 'TRANSIT_AND_TRAVEL',
  OTHER = 'OTHER',
}

/**
 * Same as {@link SiteTypeCategory}: runtime values are the API/storage enum strings.
 * For translation paths, lowercase the value to get the `site_type.subcategories.*` key.
 */
export enum SiteTypeSubcategory {
  SINGLE_FAMILY = 'SINGLE_FAMILY',
  CONDO_STRATA = 'CONDO_STRATA',
  APARTMENT = 'APARTMENT',
  LOW_RISE_MULTIPLEX = 'LOW_RISE_MULTIPLEX',
  EMPLOYEE_GUEST_CHARGING = 'EMPLOYEE_GUEST_CHARGING',
  RESTAURANT = 'RESTAURANT',
  SHOPPING = 'SHOPPING',
  ENTERTAINMENT = 'ENTERTAINMENT',
  DEALERSHIP = 'DEALERSHIP',
  HOTEL = 'HOTEL',
  PUBLIC_PARKING_LOT = 'PUBLIC_PARKING_LOT',
  ON_STREET_PARKING = 'ON_STREET_PARKING',
  HOSPITAL_MEDICAL_CENTER = 'HOSPITAL_MEDICAL_CENTER',
  SCHOOL_UNIVERSITY = 'SCHOOL_UNIVERSITY',
  RELIGIOUS_NONPROFIT = 'RELIGIOUS_NONPROFIT',
  MUNICIPAL_GOVERNMENT_BUILDING = 'MUNICIPAL_GOVERNMENT_BUILDING',
  COMPANY_VEHICLES = 'COMPANY_VEHICLES',
  SCHOOL_TRANSPORTATION = 'SCHOOL_TRANSPORTATION',
  PUBLIC_TRANSPORTATION = 'PUBLIC_TRANSPORTATION',
  DELIVERY = 'DELIVERY',
  GAS_STATION = 'GAS_STATION',
  HIGHWAY_STOP = 'HIGHWAY_STOP',
  AIRPORT = 'AIRPORT',
  OTHER_NOT_LISTED = 'OTHER_NOT_LISTED',
}

export interface SiteType {
  category: SiteTypeCategory;
  subcategory: SiteTypeSubcategory;
  otherDescription?: string;
}

/** Category → subcategory mapping for the site type dropdown. */
export const SITE_TYPE_CATEGORY_SUBCATEGORIES: Record<SiteTypeCategory, SiteTypeSubcategory[]> = {
  [SiteTypeCategory.RESIDENTIAL]: [
    SiteTypeSubcategory.SINGLE_FAMILY,
    SiteTypeSubcategory.CONDO_STRATA,
    SiteTypeSubcategory.APARTMENT,
    SiteTypeSubcategory.LOW_RISE_MULTIPLEX,
  ],
  [SiteTypeCategory.WORKPLACE]: [SiteTypeSubcategory.EMPLOYEE_GUEST_CHARGING],
  [SiteTypeCategory.RETAIL_AND_HOSPITALITY]: [
    SiteTypeSubcategory.RESTAURANT,
    SiteTypeSubcategory.SHOPPING,
    SiteTypeSubcategory.ENTERTAINMENT,
    SiteTypeSubcategory.DEALERSHIP,
    SiteTypeSubcategory.HOTEL,
  ],
  [SiteTypeCategory.PUBLIC_PARKING]: [
    SiteTypeSubcategory.PUBLIC_PARKING_LOT,
    SiteTypeSubcategory.ON_STREET_PARKING,
  ],
  [SiteTypeCategory.COMMUNITY_AND_CIVIC]: [
    SiteTypeSubcategory.HOSPITAL_MEDICAL_CENTER,
    SiteTypeSubcategory.SCHOOL_UNIVERSITY,
    SiteTypeSubcategory.RELIGIOUS_NONPROFIT,
    SiteTypeSubcategory.MUNICIPAL_GOVERNMENT_BUILDING,
  ],
  [SiteTypeCategory.FLEET_DEPOT]: [
    SiteTypeSubcategory.COMPANY_VEHICLES,
    SiteTypeSubcategory.SCHOOL_TRANSPORTATION,
    SiteTypeSubcategory.PUBLIC_TRANSPORTATION,
    SiteTypeSubcategory.DELIVERY,
  ],
  [SiteTypeCategory.TRANSIT_AND_TRAVEL]: [
    SiteTypeSubcategory.GAS_STATION,
    SiteTypeSubcategory.HIGHWAY_STOP,
    SiteTypeSubcategory.AIRPORT,
  ],
  [SiteTypeCategory.OTHER]: [SiteTypeSubcategory.OTHER_NOT_LISTED],
};

/**
 * Validates that a site type has required fields:
 * - category and subcategory are always required
 * - if subcategory is OTHER_NOT_LISTED, otherDescription is required
 */
export function isSiteTypeValid(siteType: SiteType | undefined): boolean {
  if (!siteType?.category || !siteType?.subcategory) {
    return false;
  }
  if (siteType.subcategory === SiteTypeSubcategory.OTHER_NOT_LISTED) {
    return Boolean(siteType.otherDescription?.trim());
  }
  return true;
}

/**
 * Returns the display string for a site type (for list views and dropdown header).
 * Uses the same rules everywhere: OTHER_NOT_LISTED → otherDescription or fallback key; else subcategory label.
 */
export function getSiteTypeDisplayText(
  siteType: SiteType | undefined,
  t: TFunction,
  fallbackKey = 'n_a',
): string {
  if (!siteType) {
    return t(fallbackKey);
  }
  if (siteType.subcategory === SiteTypeSubcategory.OTHER_NOT_LISTED) {
    return (
      siteType.otherDescription?.trim() ||
      t(`site_type.subcategories.${SiteTypeSubcategory.OTHER_NOT_LISTED.toLowerCase()}`)
    );
  }
  if (siteType.subcategory) {
    return t(`site_type.subcategories.${siteType.subcategory.toLowerCase()}`);
  }
  return t(fallbackKey);
}
